import React, {Component, ReactNode} from 'react';
import CustomTable, {CustomColumnProps} from '../../custom/CustomTable';
import CustomPaginator, {CustomPaginatorProps} from '../../custom/CustomPaginator';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import LanguageUtil from '../../../util/LanguageUtil';
import RoleHelper from '../../../RoleHelper';
import ComponentName from '../../../constant/ComponentName';
import {CustomListRecordRowModel, CustomListTabType} from '../../../domain/custom-list/CustomListDomains';
import Icon from '../../../icons/Icon';
import {ConfirmPopup} from 'primereact/confirmpopup';
import {customConfirm} from '../../custom/CustomPopupConfirm';
import {DataTableRowClickEventParams, DataTableSelectionChangeParams} from 'primereact/datatable';
import moment from 'moment';
import {query} from '@trlogic/client-js';
import DataType from '../../../constant/DataType';
import Order = query.Order;

interface Props {
  records: CustomListRecordRowModel[];
  selectedRecords: CustomListRecordRowModel[];
  totalRecords: number;
  currentPage: number;
  rows: number;
  recordType: CustomListTabType;
  sorts: Order[];
  onSelectionChange: (records: CustomListRecordRowModel[]) => void;
  onDelete: (record: CustomListRecordRowModel) => Promise<void>;
  onRowClick: (record: CustomListRecordRowModel) => void;
  onPageChange: (page: number, rows: number) => void;
  onSortChange: (sorts: Order[]) => void;
  onAddColumnFilter: (
    field: string,
    value: string | number,
    label: string,
    dataType: DataType,
    categorical?: boolean,
  ) => void;
}

class CustomListRecordTableComponent extends Component<Props> {
  public render(): ReactNode {
    const {records, selectedRecords, totalRecords, currentPage, rows, sorts} = this.props;

    return (
      <div className={'w-full h-full flex flex-col overflow-y-hidden'}>
        <ConfirmPopup/>
        <div
          className={
            'overflow-y-scroll w-full rounded-lg h-full xl:max-h-[calc(100vh-398px)] 2xl:max-h-[calc(100vh-428px)] '
          }
        >
          <CustomTable
            value={records}
            columns={this.getColumns()}
            selectionMode={'checkbox'}
            selection={selectedRecords}
            dataKey={'id'}
            onSelectionChange={this.onSelectionChange}
            onRowClick={this.onRowClick}
            onSortChange={this.props.onSortChange}
            multiSortMeta={sorts.map((sort) => ({
              field: sort.field,
              order: sort.operator === 'DESC' ? -1 : 1,
            }))}
          />
        </div>
        <div className={'paginator'}>
          <CustomPaginator
            rows={rows}
            totalRecords={totalRecords}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            currentPage={currentPage}
            onPageChange={this.onPageChange}
          />
        </div>
      </div>
    );
  }

  private onPageChange = (event: CustomPaginatorProps): void => {
    this.props.onPageChange(event.page, event.rows);
  };

  private onSelectionChange = (event: DataTableSelectionChangeParams): void => {
    const selection: CustomListRecordRowModel[] = Array.isArray(event.value)
      ? (event.value as CustomListRecordRowModel[])
      : [];
    this.props.onSelectionChange(selection);
  };

  private onRowClick = (event: DataTableRowClickEventParams): void => {
    this.props.onRowClick(event.data as CustomListRecordRowModel);
  };

  private getColumns = (): CustomColumnProps[] => {
    const {recordType} = this.props;
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({id: 'pages.customList.table.name', defaultMessage: 'Name'}),
        className: 'w-1/3',
        copyable: true,
        filterable: true,
        onCopy: (record: CustomListRecordRowModel) => record.name || 'N/A',
        addFilter: (record: CustomListRecordRowModel) => {
          if (record.name == undefined || record.name.length === 0) {
            return;
          }
          this.props.onAddColumnFilter(
            'name',
            record.name,
            LanguageUtil.get({id: 'pages.customList.table.name', defaultMessage: 'Name'}),
            DataType.STRING,
          );
        },
        body: (record: CustomListRecordRowModel) => (
          <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>
            <div className={'w-8 h-8 flex items-center justify-center rounded-full bg-[#F4F3FF] mr-2'}>
              <Icon icon={'person'} size={16} className={'text-f-purple'}/>
            </div>
            <CustomTableContentWrapper clipSize={40} text={record.name}/>
          </div>
        ),
      },
    ];

    if (recordType === 'individual') {
      columns.push({
        field: 'gender',
        header: LanguageUtil.get({id: 'pages.customList.table.gender', defaultMessage: 'Gender'}),
        className: 'w-1/4',
        copyable: true,
        filterable: true,
        onCopy: (record: CustomListRecordRowModel) => this.formatGender(record.gender) || 'N/A',
        addFilter: (record: CustomListRecordRowModel) => {
          if (record.gender == undefined || record.gender.length === 0) {
            return;
          }
          this.props.onAddColumnFilter(
            'gender',
            record.gender,
            LanguageUtil.get({id: 'pages.customList.table.gender', defaultMessage: 'Gender'}),
            DataType.STRING,
            true,
          );
        },
        body: (record: CustomListRecordRowModel) => (
          <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>
            <CustomTableContentWrapper text={this.formatGender(record.gender)}/>
          </div>
        ),
      });
    }

    columns.push({
      field: 'createdAt',
      header: LanguageUtil.get({id: 'pages.customList.table.createdAt', defaultMessage: 'Created At'}),
      className: 'w-1/4',
      copyable: true,
      filterable: true,
      onCopy: (record: CustomListRecordRowModel) =>
        record.createdAt ? moment(record.createdAt).format('DD.MM.YYYY HH:mm') : 'N/A',
      addFilter: (record: CustomListRecordRowModel) => {
        if (!record.createdAt) {
          return;
        }
        this.props.onAddColumnFilter(
          'createdAt',
          record.createdAt,
          LanguageUtil.get({id: 'pages.customList.table.createdAt', defaultMessage: 'Created At'}),
          DataType.DATE,
        );
      },
      body: (record: CustomListRecordRowModel) => (
        <div className={'flex items-center text-f-black-3 text-xs 2xl:text-md'}>
          <CustomTableContentWrapper
            text={record.createdAt ? moment(record.createdAt).format('DD.MM.YYYY HH:mm') : ''}
          />
        </div>
      ),
    });

    if (RoleHelper.canEdit(ComponentName.CUSTOM_LIST)) {
      columns.push({
        field: 'action',
        header: LanguageUtil.get({id: 'pages.customList.table.action', defaultMessage: 'Action'}),
        className: 'max-w-[80px]',
        sortable: false,
        body: this.getActions,
      });
    }

    return columns;
  };

  private getActions = (record: CustomListRecordRowModel): ReactNode => {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          customConfirm({
            target: e.currentTarget,
            accept: async () => await this.props.onDelete(record),
            message: LanguageUtil.get({
              id: 'messages.confirm.remove1',
              defaultMessage: 'Are you sure you want to remove?',
            }),
          });
        }}
      >
        <Icon icon={'delete'} size={20} className={'hover:text-primary text-[#607080] hover:cursor-pointer'}/>
      </div>
    );
  };

  private formatGender = (gender: string | null): string => {
    switch (gender) {
      case 'MALE':
        return 'Male';
      case 'FEMALE':
        return 'Female';
      default:
        return gender || '';
    }
  };
}

export default CustomListRecordTableComponent;
